import clsx from 'clsx';
import type { CSSProperties } from 'react';
import { FormattedNumber } from 'react-intl';

import type { GroupQuoteLeg } from '@/App/tiles/Option/strategy/groupedPricing.ts';
import { GroupPremiumValue } from '@/App/tiles/Option/strategy/GroupedPricingSummary/GroupPremiumValue.tsx';
import { getSummaryValue, isGreekField } from '@/App/tiles/Option/strategy/GroupedPricingSummary/utils.ts';
import type { StrategyField } from '@/App/tiles/Option/strategy/types.tsx';
import type { PremiumType } from '@/store/option/state.ts';

export type GroupSummaryFieldProps = {
  field: StrategyField;
  mode: 'vertical' | 'horizontal';
  legs: GroupQuoteLeg[];
  premiumType: PremiumType;
  markupCurrencyIndex: PremiumType['premiumCurrency'] | undefined;
  currency: string;
  currency1: string;
  currencyPairPrecision: number;
  isExpired: boolean;
  showPricing: boolean;
  isInternalSales: boolean;
  style?: CSSProperties;
};

export function GroupSummaryField(props: GroupSummaryFieldProps) {
  const { field, currency, currency1, style } = props;
  const value = getSummaryValue(props);
  if (field === 'premium' && value !== undefined) {
    return (
      <div
        data-e2e={`group-summary-${field}`}
        className={clsx(
          'grid-cell group-summary-field bg-lvl1 monospace-numbers fs-6 align-items-center',
          props.mode === 'horizontal' ? 'justify-content-end text-end pe-1' : 'justify-content-start px-2',
          { expired: props.isExpired },
        )}
        style={style}
      >
        <GroupPremiumValue value={value} {...props} />
      </div>
    );
  }

  return (
    <div
      data-e2e={`group-summary-${field}`}
      className={clsx(
        'grid-cell group-summary-field monospace-numbers fs-6 justify-content-start align-items-center px-1',
        field === 'premium' || field === 'markup' ? 'bg-lvl1' : 'bg-lvl2',
        {
          expired: props.isExpired,
          'text-secondary fw-bold': field === 'markup' && value !== undefined && props.isExpired,
          'text-success fw-bold': field === 'markup' && value !== undefined && !props.isExpired && value >= 0,
          'text-danger fw-bold': field === 'markup' && value !== undefined && !props.isExpired && value < 0,
        },
      )}
      style={style}
    >
      {value !== undefined && (
        <>
          {/* Preserve the legacy premium-currency label; markup values use their own currency. */}
          <FormattedNumber value={value} maximumFractionDigits={0} /> {isGreekField(field) ? currency1 : currency}
        </>
      )}
    </div>
  );
}
